/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { Body_render_report_widget_with_context } from '../models/Body_render_report_widget_with_context';
import type { Body_save_instance_page_thumbnail } from '../models/Body_save_instance_page_thumbnail';
import type { Body_save_report_page_edit_thumbnail } from '../models/Body_save_report_page_edit_thumbnail';
import type { Body_save_report_page_thumbnail } from '../models/Body_save_report_page_thumbnail';
import type { DashboardClonePageStrategy } from '../models/DashboardClonePageStrategy';
import type { EditConditionBulkUpdate } from '../models/EditConditionBulkUpdate';
import type { InstanceEditedWidgetsRead } from '../models/InstanceEditedWidgetsRead';
import type { InstanceEditsRead } from '../models/InstanceEditsRead';
import type { RenderableReport } from '../models/RenderableReport';
import type { RenderableReportEdit } from '../models/RenderableReportEdit';
import type { RenderableReportPage } from '../models/RenderableReportPage';
import type { RenderableReportWidget } from '../models/RenderableReportWidget';
import type { RenderedReportWidget } from '../models/RenderedReportWidget';
import type { ReportConditionWithPropertyLx } from '../models/ReportConditionWithPropertyLx';
import type { ReportCreate } from '../models/ReportCreate';
import type { ReportExportWithFile } from '../models/ReportExportWithFile';
import type { ReportInstanceSummary } from '../models/ReportInstanceSummary';
import type { ReportInstanceWithEntities } from '../models/ReportInstanceWithEntities';
import type { ReportInstanceWithEntitiesCreate } from '../models/ReportInstanceWithEntitiesCreate';
import type { ReportInstanceWithEntitiesUpdate } from '../models/ReportInstanceWithEntitiesUpdate';
import type { ReportPageCreate } from '../models/ReportPageCreate';
import type { ReportPageLayoutCreateBody } from '../models/ReportPageLayoutCreateBody';
import type { ReportPageLayoutRead } from '../models/ReportPageLayoutRead';
import type { ReportPageLayoutWithWidgets } from '../models/ReportPageLayoutWithWidgets';
import type { ReportPageRead } from '../models/ReportPageRead';
import type { ReportRead } from '../models/ReportRead';
import type { ReportSaveResult } from '../models/ReportSaveResult';
import type { ReportUpdate } from '../models/ReportUpdate';
import type { ReportWidgetRead } from '../models/ReportWidgetRead';
import type { ReportWithPages } from '../models/ReportWithPages';
import type { ReportWithPagesCreate } from '../models/ReportWithPagesCreate';
import type { SaveInstanceEditsRequest } from '../models/SaveInstanceEditsRequest';
import type { SignedMessageKeyDomain } from '../models/SignedMessageKeyDomain';
import type { SourceDashboardRead } from '../models/SourceDashboardRead';

import type { CancelablePromise } from '../core/CancelablePromise';
import type { BaseHttpRequest } from '../core/BaseHttpRequest';

export class ReportsService {

    constructor(public readonly httpRequest: BaseHttpRequest) {}

    /**
     * Create Report For Entity
     * @param entityId
     * @param requestBody
     * @returns ReportRead Successful Response
     * @throws ApiError
     */
    public createReportForEntity(
        entityId: string,
        requestBody: ReportCreate,
    ): CancelablePromise<ReportRead> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/reports/create-report-for-entity/{entity_id}',
            path: {
                'entity_id': entityId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Get Report
     * @param reportId
     * @returns ReportWithPages Successful Response
     * @throws ApiError
     */
    public getReport(
        reportId: string,
    ): CancelablePromise<ReportWithPages> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/reports/get-report/{report_id}',
            path: {
                'report_id': reportId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Create Report Page
     * @param reportId
     * @param requestBody
     * @returns ReportPageRead Successful Response
     * @throws ApiError
     */
    public createReportPage(
        reportId: string,
        requestBody: ReportPageCreate,
    ): CancelablePromise<ReportPageRead> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/reports/create-report-page',
            query: {
                'report_id': reportId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Delete Report
     * @param reportId
     * @returns any Successful Response
     * @throws ApiError
     */
    public deleteReport(
        reportId: string,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'DELETE',
            url: '/reports/delete-report/{report_id}',
            path: {
                'report_id': reportId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Update Report
     * @param reportId
     * @param requestBody
     * @returns ReportRead Successful Response
     * @throws ApiError
     */
    public updateReport(
        reportId: string,
        requestBody: ReportUpdate,
    ): CancelablePromise<ReportRead> {
        return this.httpRequest.request({
            method: 'PATCH',
            url: '/reports/update-report/{report_id}',
            path: {
                'report_id': reportId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Delete Report Page
     * @param pageId
     * @param reportId
     * @returns any Successful Response
     * @throws ApiError
     */
    public deleteReportPage(
        pageId: string,
        reportId: string,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'DELETE',
            url: '/reports/delete-report-page/{page_id}',
            path: {
                'page_id': pageId,
            },
            query: {
                'report_id': reportId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Create Report Page From Dashboard Template
     * @param reportId
     * @param dashboardTemplateId
     * @param requestBody
     * @returns RenderableReportPage Successful Response
     * @throws ApiError
     */
    public createReportPageFromDashboardTemplate(
        reportId: string,
        dashboardTemplateId: string,
        requestBody: DashboardClonePageStrategy,
    ): CancelablePromise<Array<RenderableReportPage>> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/reports/create-report-page-from-dashboard-template/{report_id}/{dashboard_template_id}',
            path: {
                'report_id': reportId,
                'dashboard_template_id': dashboardTemplateId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Get Renderable Report
     * @param reportId
     * @param withEdits
     * @param entityId
     * @param conditionId
     * @returns RenderableReport Successful Response
     * @throws ApiError
     */
    public getRenderableReport(
        reportId: string,
        withEdits: boolean,
        entityId?: (string | null),
        conditionId?: (string | null),
    ): CancelablePromise<RenderableReport> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/reports/get-renderable-report/{report_id}',
            path: {
                'report_id': reportId,
            },
            query: {
                'with_edits': withEdits,
                'entity_id': entityId,
                'condition_id': conditionId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Get Report Pages
     * Get a template report's page list with empty widget arrays.
     *
     * Used by the editor's initial load — the client lazy-fetches each page's
     * widgets via ``/get-renderable-report-page/{report_id}/{page_id}``.
     * @param reportId
     * @returns RenderableReport Successful Response
     * @throws ApiError
     */
    public getReportPages(
        reportId: string,
    ): CancelablePromise<RenderableReport> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/reports/get-report-pages/{report_id}',
            path: {
                'report_id': reportId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Get Renderable Report Page
     * Get a single template page's widgets (paired with /get-report-pages).
     * @param reportId
     * @param pageId
     * @returns RenderableReportPage Successful Response
     * @throws ApiError
     */
    public getRenderableReportPage(
        reportId: string,
        pageId: string,
    ): CancelablePromise<RenderableReportPage> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/reports/get-renderable-report-page/{report_id}/{page_id}',
            path: {
                'report_id': reportId,
                'page_id': pageId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Generate Report Instance Pdf
     * The export's as-of ceiling is the instance's pinned as_of_date, read in the actor. Not a caller input.
     * @param reportInstanceId
     * @param reportQuality
     * @param entityLimit
     * @param outputFormat
     * @returns SignedMessageKeyDomain Successful Response
     * @throws ApiError
     */
    public generateReportInstancePdf(
        reportInstanceId: string,
        reportQuality: number = 5,
        entityLimit?: (number | null),
        outputFormat: string = 'pdf',
    ): CancelablePromise<SignedMessageKeyDomain> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/reports/generate-pdf/{report_instance_id}',
            path: {
                'report_instance_id': reportInstanceId,
            },
            query: {
                'report_quality': reportQuality,
                'entity_limit': entityLimit,
                'output_format': outputFormat,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Save Report
     * @param reportId
     * @param requestBody
     * @returns ReportSaveResult Successful Response
     * @throws ApiError
     */
    public saveReport(
        reportId: string,
        requestBody: ReportWithPagesCreate,
    ): CancelablePromise<ReportSaveResult> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/reports/save-report',
            query: {
                'report_id': reportId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Save Report Page Thumbnail
     * Persist a client-generated JPEG thumbnail for a template report page.
     * @param reportId
     * @param pageId
     * @param formData
     * @returns any Successful Response
     * @throws ApiError
     */
    public saveReportPageThumbnail(
        reportId: string,
        pageId: string,
        formData: Body_save_report_page_thumbnail,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/reports/save-report-page-thumbnail/{report_id}/{page_id}',
            path: {
                'report_id': reportId,
                'page_id': pageId,
            },
            formData: formData,
            mediaType: 'multipart/form-data',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Save Report Page Edit Thumbnail
     * Persist a client-generated JPEG thumbnail for a condition-added page.
     *
     * Condition-added pages are stored in ``ReportPageEdit`` (action='add') and
     * don't have a ``ReportPage`` row until an instance is materialized — their
     * thumbnails are stored directly on the page edit and then copied onto new
     * instance pages by ``_seed_instance_page_thumbnails_from_sources``.
     * @param reportId
     * @param pageEditId
     * @param formData
     * @returns any Successful Response
     * @throws ApiError
     */
    public saveReportPageEditThumbnail(
        reportId: string,
        pageEditId: string,
        formData: Body_save_report_page_edit_thumbnail,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/reports/save-report-page-edit-thumbnail/{report_id}/{page_edit_id}',
            path: {
                'report_id': reportId,
                'page_edit_id': pageEditId,
            },
            formData: formData,
            mediaType: 'multipart/form-data',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Create Report Instance With Entities
     * @param reportId
     * @param requestBody
     * @returns ReportInstanceWithEntities Successful Response
     * @throws ApiError
     */
    public createReportInstanceWithEntities(
        reportId: string,
        requestBody: ReportInstanceWithEntitiesCreate,
    ): CancelablePromise<ReportInstanceWithEntities> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/reports/create-report-instance',
            query: {
                'report_id': reportId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Get Report Instance With Entities
     * @param reportInstanceId
     * @returns ReportInstanceWithEntities Successful Response
     * @throws ApiError
     */
    public getReportInstanceWithEntities(
        reportInstanceId: string,
    ): CancelablePromise<ReportInstanceWithEntities> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/reports/get-report-instance/{report_instance_id}',
            path: {
                'report_instance_id': reportInstanceId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Report Instances With Entities
     * @param reportId
     * @returns ReportInstanceWithEntities Successful Response
     * @throws ApiError
     */
    public listReportInstancesWithEntities(
        reportId: string,
    ): CancelablePromise<Array<ReportInstanceWithEntities>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/reports/list-report-instances/{report_id}',
            path: {
                'report_id': reportId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Update Report Instance With Entities
     * @param reportInstanceId
     * @param requestBody
     * @returns ReportInstanceWithEntities Successful Response
     * @throws ApiError
     */
    public updateReportInstanceWithEntities(
        reportInstanceId: string,
        requestBody: ReportInstanceWithEntitiesUpdate,
    ): CancelablePromise<ReportInstanceWithEntities> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/reports/update-report-instance/{report_instance_id}',
            path: {
                'report_instance_id': reportInstanceId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Delete Report Instance With Entities
     * @param reportInstanceId
     * @returns any Successful Response
     * @throws ApiError
     */
    public deleteReportInstanceWithEntities(
        reportInstanceId: string,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'DELETE',
            url: '/reports/delete-report-instance/{report_instance_id}',
            path: {
                'report_instance_id': reportInstanceId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Report Exports
     * @param reportInstanceId
     * @returns ReportExportWithFile Successful Response
     * @throws ApiError
     */
    public listReportExports(
        reportInstanceId: string,
    ): CancelablePromise<Array<ReportExportWithFile>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/reports/list-report-exports/{report_instance_id}',
            path: {
                'report_instance_id': reportInstanceId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Create Report Condition
     * @param reportId
     * @param requestBody
     * @returns ReportConditionWithPropertyLx Successful Response
     * @throws ApiError
     */
    public createReportCondition(
        reportId: string,
        requestBody: RenderableReportEdit,
    ): CancelablePromise<Array<ReportConditionWithPropertyLx>> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/reports/report-conditions/{report_id}/create',
            path: {
                'report_id': reportId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Report Conditions
     * @param reportId
     * @returns ReportConditionWithPropertyLx Successful Response
     * @throws ApiError
     */
    public listReportConditions(
        reportId: string,
    ): CancelablePromise<Array<ReportConditionWithPropertyLx>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/reports/report-conditions/{report_id}',
            path: {
                'report_id': reportId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Delete Report Condition
     * @param conditionId
     * @param reportId
     * @returns any Successful Response
     * @throws ApiError
     */
    public deleteReportCondition(
        conditionId: string,
        reportId: string,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'DELETE',
            url: '/reports/report-conditions/{condition_id}',
            path: {
                'condition_id': conditionId,
            },
            query: {
                'report_id': reportId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Update Report Condition
     * @param conditionId
     * @param reportId
     * @param requestBody
     * @returns ReportConditionWithPropertyLx Successful Response
     * @throws ApiError
     */
    public updateReportCondition(
        conditionId: string,
        reportId: string,
        requestBody: RenderableReportEdit,
    ): CancelablePromise<Array<ReportConditionWithPropertyLx>> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/reports/report-conditions/{condition_id}',
            path: {
                'condition_id': conditionId,
            },
            query: {
                'report_id': reportId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Update Report Widget Cloned From
     * Update the widget_cloned_from_id field for a report widget (staff only)
     * @param widgetId
     * @param widgetClonedFromId
     * @returns ReportWidgetRead Successful Response
     * @throws ApiError
     */
    public updateReportWidgetClonedFrom(
        widgetId: string,
        widgetClonedFromId?: (string | null),
    ): CancelablePromise<ReportWidgetRead> {
        return this.httpRequest.request({
            method: 'PATCH',
            url: '/reports/report-widget/{widget_id}/update-cloned-from',
            path: {
                'widget_id': widgetId,
            },
            query: {
                'widget_cloned_from_id': widgetClonedFromId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Bulk Update Report Conditions
     * @param reportId
     * @param requestBody
     * @returns ReportConditionWithPropertyLx Successful Response
     * @throws ApiError
     */
    public bulkUpdateReportConditions(
        reportId: string,
        requestBody: EditConditionBulkUpdate,
    ): CancelablePromise<Array<ReportConditionWithPropertyLx>> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/reports/report-conditions/{report_id}/bulk-update',
            path: {
                'report_id': reportId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Get Renderable Report For Instance All
     * @param reportInstanceId
     * @returns RenderableReport Successful Response
     * @throws ApiError
     */
    public getRenderableReportForInstanceAll(
        reportInstanceId: string,
    ): CancelablePromise<RenderableReport> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/reports/get-renderable-report-for-instance/{report_instance_id}',
            path: {
                'report_instance_id': reportInstanceId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Get Report Instance Pages
     * Get an instance's page list with empty widget arrays.
     *
     * Lightweight counterpart to ``/get-renderable-report-for-instance/{id}``.
     * @param reportInstanceId
     * @returns RenderableReport Successful Response
     * @throws ApiError
     */
    public getReportInstancePages(
        reportInstanceId: string,
    ): CancelablePromise<RenderableReport> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/reports/get-report-instance-pages/{report_instance_id}',
            path: {
                'report_instance_id': reportInstanceId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Get Renderable Report Instance Page
     * Get a single instance page's widgets (paired with /get-report-instance-pages).
     * @param reportInstanceId
     * @param instancePageId
     * @returns RenderableReportPage Successful Response
     * @throws ApiError
     */
    public getRenderableReportInstancePage(
        reportInstanceId: string,
        instancePageId: string,
    ): CancelablePromise<RenderableReportPage> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/reports/get-renderable-report-instance-page/{report_instance_id}/{instance_page_id}',
            path: {
                'report_instance_id': reportInstanceId,
                'instance_page_id': instancePageId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Save Instance Edits All
     * @param reportInstanceId
     * @param requestBody
     * @returns InstanceEditedWidgetsRead Successful Response
     * @throws ApiError
     */
    public saveInstanceEditsAll(
        reportInstanceId: string,
        requestBody: SaveInstanceEditsRequest,
    ): CancelablePromise<InstanceEditedWidgetsRead> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/reports/instance-edits/{report_instance_id}/save',
            path: {
                'report_instance_id': reportInstanceId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Save Instance Page Thumbnail
     * Persist a client-generated JPEG thumbnail for an instance report page.
     * @param reportInstanceId
     * @param instancePageId
     * @param formData
     * @returns any Successful Response
     * @throws ApiError
     */
    public saveInstancePageThumbnail(
        reportInstanceId: string,
        instancePageId: string,
        formData: Body_save_instance_page_thumbnail,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/reports/save-instance-page-thumbnail/{report_instance_id}/{instance_page_id}',
            path: {
                'report_instance_id': reportInstanceId,
                'instance_page_id': instancePageId,
            },
            formData: formData,
            mediaType: 'multipart/form-data',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Get Edited Widgets All
     * @param reportInstanceId
     * @returns InstanceEditedWidgetsRead Successful Response
     * @throws ApiError
     */
    public getEditedWidgetsAll(
        reportInstanceId: string,
    ): CancelablePromise<InstanceEditedWidgetsRead> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/reports/instance-edits/{report_instance_id}/edited-widgets',
            path: {
                'report_instance_id': reportInstanceId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Get Renderable Report For Instance
     * @param reportInstanceId
     * @param entityId
     * @returns RenderableReport Successful Response
     * @throws ApiError
     */
    public getRenderableReportForInstance(
        reportInstanceId: string,
        entityId: string,
    ): CancelablePromise<RenderableReport> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/reports/get-renderable-report-for-instance/{report_instance_id}/{entity_id}',
            path: {
                'report_instance_id': reportInstanceId,
                'entity_id': entityId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Save Instance Edits
     * @param reportInstanceId
     * @param entityId
     * @param requestBody
     * @returns InstanceEditsRead Successful Response
     * @throws ApiError
     */
    public saveInstanceEdits(
        reportInstanceId: string,
        entityId: string,
        requestBody: RenderableReport,
    ): CancelablePromise<InstanceEditsRead> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/reports/instance-edits/{report_instance_id}/{entity_id}/save',
            path: {
                'report_instance_id': reportInstanceId,
                'entity_id': entityId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Get Instance Edits
     * @param reportInstanceId
     * @param entityId
     * @returns InstanceEditsRead Successful Response
     * @throws ApiError
     */
    public getInstanceEdits(
        reportInstanceId: string,
        entityId: string,
    ): CancelablePromise<InstanceEditsRead> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/reports/instance-edits/{report_instance_id}/{entity_id}',
            path: {
                'report_instance_id': reportInstanceId,
                'entity_id': entityId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Delete Instance Edits
     * @param reportInstanceId
     * @param entityId
     * @returns any Successful Response
     * @throws ApiError
     */
    public deleteInstanceEdits(
        reportInstanceId: string,
        entityId: string,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'DELETE',
            url: '/reports/instance-edits/{report_instance_id}/{entity_id}',
            path: {
                'report_instance_id': reportInstanceId,
                'entity_id': entityId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Delete Instance Widget Edit
     * @param widgetEditId
     * @returns any Successful Response
     * @throws ApiError
     */
    public deleteInstanceWidgetEdit(
        widgetEditId: string,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'DELETE',
            url: '/reports/instance-edits/widget-edit/{widget_edit_id}',
            path: {
                'widget_edit_id': widgetEditId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Revert Instance To Template
     * @param reportInstanceId
     * @returns any Successful Response
     * @throws ApiError
     */
    public revertInstanceToTemplate(
        reportInstanceId: string,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/reports/instance-edits/{report_instance_id}/revert-to-template',
            path: {
                'report_instance_id': reportInstanceId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Get Edited Widgets
     * @param reportInstanceId
     * @param entityId
     * @returns InstanceEditedWidgetsRead Successful Response
     * @throws ApiError
     */
    public getEditedWidgets(
        reportInstanceId: string,
        entityId: string,
    ): CancelablePromise<InstanceEditedWidgetsRead> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/reports/instance-edits/{report_instance_id}/{entity_id}/edited-widgets',
            path: {
                'report_instance_id': reportInstanceId,
                'entity_id': entityId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Get Report Context
     * Get comprehensive report context for materialized pointers.
     *
     * Returns all available context values that can be used in rich text widgets:
     * @?page-number, @?total-pages, @?report-name, @?current-date, @?effective-date, @?entity-name
     * @param reportId
     * @param pageNumber
     * @param totalPages
     * @param relativeToDate
     * @param entityId
     * @returns any Successful Response
     * @throws ApiError
     */
    public getReportContext(
        reportId: string,
        pageNumber: number = 1,
        totalPages: number = 1,
        relativeToDate?: (string | null),
        entityId?: (string | null),
    ): CancelablePromise<Record<string, any>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/reports/get-report-context/{report_id}',
            path: {
                'report_id': reportId,
            },
            query: {
                'page_number': pageNumber,
                'total_pages': totalPages,
                'relative_to_date': relativeToDate,
                'entity_id': entityId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Render Report Widget With Context
     * Render a report widget with additional report context for materialized pointers.
     *
     * This endpoint enables rich text widgets to use materialized pointers like:
     * @?page-number, @?current-date, @?relative-to-date
     * @param reportId
     * @param requestBody
     * @param pageNumber
     * @param relativeToDate
     * @param entityId
     * @param globalAsOfDate
     * @returns RenderedReportWidget Successful Response
     * @throws ApiError
     */
    public renderReportWidgetWithContext(
        reportId: string,
        requestBody: Body_render_report_widget_with_context,
        pageNumber: number = 1,
        relativeToDate?: (string | null),
        entityId?: (string | null),
        globalAsOfDate?: (string | null),
    ): CancelablePromise<RenderedReportWidget> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/reports/render-report-widget-with-context',
            query: {
                'page_number': pageNumber,
                'relative_to_date': relativeToDate,
                'entity_id': entityId,
                'global_as_of_date': globalAsOfDate,
                'report_id': reportId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Create Report Layout
     * Create a new report layout.
     * @param reportId
     * @param requestBody
     * @returns ReportPageLayoutRead Successful Response
     * @throws ApiError
     */
    public createReportLayout(
        reportId: string,
        requestBody: ReportPageLayoutCreateBody,
    ): CancelablePromise<ReportPageLayoutRead> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/reports/reports/{report_id}/layouts',
            path: {
                'report_id': reportId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Report Layouts
     * List all layouts for a report.
     * @param reportId
     * @returns ReportPageLayoutRead Successful Response
     * @throws ApiError
     */
    public listReportLayouts(
        reportId: string,
    ): CancelablePromise<Array<ReportPageLayoutRead>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/reports/reports/{report_id}/layouts',
            path: {
                'report_id': reportId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Get Report Layout
     * Get a report layout with its widgets.
     * @param reportId
     * @param layoutId
     * @returns ReportPageLayoutWithWidgets Successful Response
     * @throws ApiError
     */
    public getReportLayout(
        reportId: string,
        layoutId: string,
    ): CancelablePromise<ReportPageLayoutWithWidgets> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/reports/reports/{report_id}/layouts/{layout_id}',
            path: {
                'report_id': reportId,
                'layout_id': layoutId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Update Report Layout
     * Update a report layout.
     * @param reportId
     * @param layoutId
     * @param requestBody
     * @returns ReportPageLayoutRead Successful Response
     * @throws ApiError
     */
    public updateReportLayout(
        reportId: string,
        layoutId: string,
        requestBody: ReportPageLayoutCreateBody,
    ): CancelablePromise<ReportPageLayoutRead> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/reports/reports/{report_id}/layouts/{layout_id}',
            path: {
                'report_id': reportId,
                'layout_id': layoutId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Delete Report Layout
     * Delete a report layout.
     * @param reportId
     * @param layoutId
     * @returns any Successful Response
     * @throws ApiError
     */
    public deleteReportLayout(
        reportId: string,
        layoutId: string,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'DELETE',
            url: '/reports/reports/{report_id}/layouts/{layout_id}',
            path: {
                'report_id': reportId,
                'layout_id': layoutId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Assign Layout To Page
     * Assign or unassign a layout to/from a page.
     * @param reportId
     * @param pageId
     * @param requestBody
     * @returns any Successful Response
     * @throws ApiError
     */
    public assignLayoutToPage(
        reportId: string,
        pageId: string,
        requestBody?: (string | null),
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/reports/reports/{report_id}/pages/{page_id}/assign-layout',
            path: {
                'report_id': reportId,
                'page_id': pageId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Save Layout Widgets
     * Save widgets for a layout.
     * @param reportId
     * @param layoutId
     * @param requestBody
     * @returns any Successful Response
     * @throws ApiError
     */
    public saveLayoutWidgets(
        reportId: string,
        layoutId: string,
        requestBody: Array<RenderableReportWidget>,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/reports/reports/{report_id}/layouts/{layout_id}/widgets',
            path: {
                'report_id': reportId,
                'layout_id': layoutId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Source Dashboards
     * @param reportId
     * @returns SourceDashboardRead Successful Response
     * @throws ApiError
     */
    public listSourceDashboards(
        reportId: string,
    ): CancelablePromise<Array<SourceDashboardRead>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/reports/reports/{report_id}/source-dashboards',
            path: {
                'report_id': reportId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Sync Dashboard To Report
     * @param reportId
     * @param dashboardId
     * @returns any Successful Response
     * @throws ApiError
     */
    public syncDashboardToReport(
        reportId: string,
        dashboardId: string,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/reports/reports/{report_id}/sync-from-dashboard/{dashboard_id}',
            path: {
                'report_id': reportId,
                'dashboard_id': dashboardId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Reports For Entity
     * @param entityId
     * @returns ReportRead Successful Response
     * @throws ApiError
     */
    public listReportsForEntity(
        entityId: string,
    ): CancelablePromise<Array<ReportRead>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/entities/{entity_id}/reports/list-reports-for-entity',
            path: {
                'entity_id': entityId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Reports For Cluster
     * @param clusterId
     * @returns ReportRead Successful Response
     * @throws ApiError
     */
    public listReportsForCluster(
        clusterId: string,
    ): CancelablePromise<Array<ReportRead>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/clusters/{cluster_id}/reports/list-reports-for-cluster',
            path: {
                'cluster_id': clusterId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Bulk List Report Instances
     * @param clusterId
     * @param requestBody
     * @returns ReportInstanceSummary Successful Response
     * @throws ApiError
     */
    public bulkListReportInstances(
        clusterId: string,
        requestBody: Array<string>,
    ): CancelablePromise<Record<string, Array<ReportInstanceSummary>>> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/clusters/{cluster_id}/reports/bulk-list-report-instances',
            path: {
                'cluster_id': clusterId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Create Report For Cluster
     * @param clusterId
     * @param requestBody
     * @returns ReportRead Successful Response
     * @throws ApiError
     */
    public createReportForCluster(
        clusterId: string,
        requestBody: ReportCreate,
    ): CancelablePromise<ReportRead> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/clusters/{cluster_id}/reports/create-report-for-cluster',
            path: {
                'cluster_id': clusterId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

}
